import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Button, Dropdown, Icon, message } from '@sm/ide-components';
import { useChatStore } from '@/store';
import ChatHeader, { type HeaderTab } from '../ChatHeader';
import StoreMarketing from '../StoreMarketing';
import MessageList from '../MessageList';
import AutoScrollContainer from '../AutoScrollContainer';
import QuickQuestions from '../QuickQuestions';
import ChatInput from '../ChatInput';
import HistoryPanel from '../HistoryPanel';
import type { DeepPartial, QuickQuestion } from '@/types/chat';
import { ERole, type ChatMessage } from '@/types/chat';
import { InboundInit } from '@/types';
import {
  iceBreaker,
  sessionStart,
  recordChat,
  summarizeData,
  recommendQuestions,
  analysisCancelTask,
  queryStoreMarketingVisible,
} from '@/services';
import { t } from '@/i18n';
import { getChatFlowStrategy, executeFlow } from '@/services/chatFlow';
import { useAbortableRequest } from '@/hooks';
import toast from '../Message';
import { isAbortError } from '@/utils';
import { emitGa, trackAnswerDisplay, type AskQuestionSource } from '@/utils/ga';
import './index.less';

type PanelView = 'chat' | 'history';

export interface ChatPanelProps {
  config?: InboundInit['payload']['config'];
  onClose?: () => void;
  isReady?: boolean;
  fullScreen?: boolean;
  onFullScreen?: () => void;
  /** init.source。Mind 时输入框启用 @ 引用 */
  hostSource?: string;
  /** SDK 抽屉：推荐问题曝光等到抽屉打开。整页面板不传 */
  impressionOnOpen?: boolean;
}

const ChatPanel: React.FC<ChatPanelProps> = ({
  config,
  onClose,
  isReady = false,
  onFullScreen,
  hostSource,
  impressionOnOpen = false,
}) => {
  const {
    createSession,
    getCurrentSession,
    addMessage: addMessageRaw,
    updateMessage: updateMessageRaw,
    getMessage,
    setLoading,
    isLoading,
    closeDrawer,
    sessionInfo,
    setSessionInfo,
    getDevSession,
    getBossSession,
    quickQuestions,
    setQuickQuestions,
    currentRole,
    setStreaming,
    isStreaming,
    loadHistory,
    openHistoryDetail,
    historyScope,
    isHistoryReadonly,
    isOpen,
  } = useChatStore();

  const [view, setView] = useState<PanelView>('chat');
  const [headerTab, setHeaderTab] = useState<HeaderTab>('chat');
  const [isCancelling, setIsCancelling] = useState(false);
  const [sendScrollTick, setSendScrollTick] = useState(0);
  const requestGenRef = useRef(0);
  const {
    begin: beginCancelableRequest,
    abort: abortCurrentRequest,
    clear: clearCancelableRequest,
  } = useAbortableRequest();

  const currentSession = getCurrentSession();
  const messages = currentSession?.messages ?? [];

  const addMessage = useCallback((msg: Omit<ChatMessage, 'id' | 'timestamp'>) => {
    const id = addMessageRaw(msg);
    if (msg.role === 'assistant') {
      trackAnswerDisplay(id, msg.status, msg.content);
    }
    return id;
  }, [addMessageRaw]);

  const updateMessage = useCallback((id: string, updates: DeepPartial<ChatMessage>) => {
    updateMessageRaw(id, updates);
    if (updates.status || updates.content) {
      const msg = getMessage(id);
      if (msg?.role === 'assistant') {
        trackAnswerDisplay(id, msg.status, msg.content);
      }
    }
  }, [updateMessageRaw, getMessage]);

  // 提取会话参数
  const extractSessionParams = useCallback(() => {
    const devSession = getDevSession();
    const bossSession = getBossSession();
    return {
      accessToken: devSession?.accessToken ?? '',
      chatId: String(devSession?.chatId ?? ''),
      sessionId: bossSession?.sessionId ?? '',
    };
  }, [getDevSession, getBossSession]);

  // 创建代数计数器和受保护的快速问题更新函数
  const createGenerationCounter = useCallback(() => {
    const gen = ++requestGenRef.current;
    return {
      gen,
      guardedSetQuickQuestions: (qs: string[]) => {
        if (requestGenRef.current === gen) setQuickQuestions(qs);
      },
    };
  }, [setQuickQuestions]);

  const initializeSession = useCallback(async () => {
    try {
      const session = await sessionStart();
      if (session) {
        setSessionInfo({
          type: 'dev-center',
          accessToken: session.access_token,
          chatId: session.chat_id,
        });
      }
    } catch {
      // 会话初始化失败时仍允许继续提问，由后续请求自行报错
    }

    iceBreaker()
      .then(({ questions }) => setQuickQuestions(questions))
      .catch(() => { });
  }, [setSessionInfo, setQuickQuestions]);

  const startNewChat = useCallback(async () => {
    const current = getCurrentSession();
    if (current && current.messages.length === 0 && !isHistoryReadonly) return;

    abortCurrentRequest();
    setLoading(false);
    setStreaming(false);
    createSession();
    setSessionInfo(null);
    setQuickQuestions([]);

    if (currentRole.id === ERole.ANALYST) {
      await initializeSession();
    } else if (currentRole.id === ERole.BUSINESS_ANALYSIS) {
      iceBreaker()
        .then(({ questions }) => setQuickQuestions(questions))
        .catch(() => { });
    }
  }, [
    getCurrentSession,
    abortCurrentRequest,
    setLoading,
    setStreaming,
    createSession,
    setSessionInfo,
    setQuickQuestions,
    currentRole.id,
    initializeSession,
    isHistoryReadonly,
  ]);

  const isBusy = isLoading || isStreaming || isCancelling;

  const handleNewChat = useCallback(() => {
    if (isBusy) return;
    setView('chat');
    void startNewChat();
  }, [isBusy, startNewChat]);

  const handleShowHistory = useCallback(() => {
    if (isBusy) return;
    setView((prev) => {
      if (prev === 'history') return 'chat';
      void loadHistory(true);
      return 'history';
    });
  }, [isBusy, loadHistory]);

  const handleSelectHistory = useCallback((sessionId: string) => {
    if (isBusy) return;
    abortCurrentRequest();
    setLoading(false);
    setStreaming(false);
    openHistoryDetail(sessionId);
    setQuickQuestions([]);
    setView('chat');
  }, [
    isBusy,
    abortCurrentRequest,
    setLoading,
    setStreaming,
    openHistoryDetail,
    setQuickQuestions,
  ]);

  const handleTerminate = useCallback(async () => {
    if (isCancelling) return;

    setIsCancelling(true);

    // ANALYST 角色：直接取消，不用调用接口
    if (currentRole.id === ERole.ANALYST) {
      abortCurrentRequest();
      setIsCancelling(false);
      return;
    }

    // BUSINESS_ANALYSIS 角色：调用接口取消
    const { taskId, sessionInfo } = useChatStore.getState();
    const sessionId = sessionInfo?.type === 'boss-report' ? sessionInfo.sessionId : undefined;

    if (!taskId) {
      setIsCancelling(false);
      return;
    }

    try {
      const result = await analysisCancelTask(taskId, sessionId);
      if (!result.success) {
        toast.error(t('chat.cancelFailed'));
      } else {
        abortCurrentRequest();
      }
    } catch {
      toast.error(t('chat.cancelFailed'));
    } finally {
      setIsCancelling(false);
    }
  }, [isCancelling, currentRole.id, abortCurrentRequest]);

  /** 初始化：等 token/tenantCode 就绪后开启新会话（破冰逻辑统一交由角色副作用处理，避免重复请求） */
  useEffect(() => {
    if (!isReady) return;

    if (!getCurrentSession()) {
      createSession();
    }

    return () => {
      setSessionInfo(null);
      setQuickQuestions([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, historyScope]);

  /** 根据角色拉取破冰/推荐问题：初次进入、切换角色、换店（historyScope）时触发 */
  useEffect(() => {
    if (!isReady) return;

    // ANALYST：sessionStart + iceBreaker；BUSINESS_ANALYSIS：仅 iceBreaker
    // 历史详情续聊已有消息，不再拉破冰问题
    if (currentRole.id === ERole.ANALYST) {
      initializeSession();
    } else if (currentRole.id === ERole.BUSINESS_ANALYSIS) {
      const session = getCurrentSession();
      if (session?.messages.length) {
        setQuickQuestions([]);
        return;
      }
      iceBreaker()
        .then(({ questions }) => setQuickQuestions(questions))
        .catch(() => { });
    }
    return () => {
      setQuickQuestions([]);
    };
  }, [isReady, historyScope, currentRole.id, initializeSession, getCurrentSession, setQuickQuestions]);

  const handleClose = useCallback(() => {
    (onClose ?? closeDrawer)();
  }, [onClose, closeDrawer]);

  const handleFullScreen = useCallback(() => {
    onFullScreen?.();
  }, [onFullScreen]);

  const handleSend = useCallback(
    async (text: string, source: AskQuestionSource | 'faq' = 'input') => {
      if (isLoading) return;
      if (isHistoryReadonly) return;

      if (source !== 'faq') {
        emitGa('ask_question', { question_text: text, source });
      }

      if (view === 'history') {
        setView('chat');
        await startNewChat();
      }

      // 用户主动发送时强制回到底部，并恢复自动滚动。
      setSendScrollTick((tick) => tick + 1);

      const params = extractSessionParams();
      // 本次请求的代数，recommendQuestions 回调只在代数匹配时才写入
      const { guardedSetQuickQuestions } = createGenerationCounter();
      const controller = beginCancelableRequest();
      const strategy = getChatFlowStrategy(currentRole.schema);

      setLoading(true);
      try {
        await executeFlow(
          strategy,
          text,
          {
            ...params,
            addMessage,
            updateMessage,
            setLoading,
            setQuickQuestions: guardedSetQuickQuestions,
            signal: controller.signal,
          },
          {
            timeoutMs: 10 * 60 * 1000,
            onTimeout: () => controller.abort(),
          },
        );
      } finally {
        clearCancelableRequest(controller);
      }
    },
    [
      isLoading,
      isHistoryReadonly,
      view,
      startNewChat,
      sessionInfo,
      currentRole.id,
      currentRole.schema,
      addMessage,
      updateMessage,
      setLoading,
      setQuickQuestions,
    ],
  );

  /**
   * 分段模式第二阶段：用户点击「生成图表」后触发。
   *
   * - 不依赖全局 isLoading：图表生成与消息发送相互独立，
   *   每张卡片通过自身的 isGeneratingChart 控制按钮 loading 态
   * - sqlChatId / sqlRecordId 来自点击卡片自身存储的上下文，
   *   与当前 sessionInfo 无关，确保切换模式后依然指向正确的查询记录
   * - recommendQuestions 结果纳入代数管理，防止滞后响应覆盖新一轮发送的状态
   */
  const handleGenerateChart = useCallback(
    async (messageId: string, sqlRecordId: string, sqlChatId: string) => {
      const { accessToken } = extractSessionParams();
      const { guardedSetQuickQuestions } = createGenerationCounter();
      const controller = beginCancelableRequest();

      emitGa('ask_question', { question_text: t('chart.generate'), source: 'generate_chart' });

      setLoading(true);
      let analyzingMessageId: string | null = null;
      try {
        // 先添加分析中的消息
        analyzingMessageId = addMessage({
          role: 'assistant',
          bizPayload: {
            role: currentRole.id,
            accessToken: sessionInfo?.type === 'dev-center' ? sessionInfo.accessToken : undefined,
            chatId: sessionInfo?.type === 'dev-center' ? sessionInfo.chatId : undefined,
          },
          content: { text: t('chat.analyzing') },
          status: 'sending',
        });

        const result = await recordChat(sqlChatId, sqlRecordId, accessToken, controller.signal);

        if (result.success === false) {
          updateMessage(analyzingMessageId, {
            content: { text: t('chat.answerFailed') },
            status: 'error',
          });
          throw new Error('chart generation failed');
        }

        updateMessage(messageId, { awaitingChart: false });

        const isTableChart = result.chart?.type === 'table';
        // 更新分析中的消息为最终的图表内容
        updateMessage(analyzingMessageId, {
          content: {
            chart: result.chart
              ? {
                type: result.chart.type,
                title: result.chart.title,
                dimensions: result.chart.dimensions,
                metrics: result.chart.metrics,
                series: result.chart.series,
                data: result.data ?? result.chart.data,
              }
              : undefined,
            // table 类型需要原始数据行渲染表格；其他图表类型 chartOnly=true 隐藏切换按钮
            tableData: isTableChart ? (result.data ?? []) : undefined,
            chartOnly: !isTableChart,
          },
          status: 'success',
          recordId: result.chat_record_id,
        });

        if (result.chat_record_id) {
          recommendQuestions(result.chat_record_id, accessToken, controller.signal)
            .then((qs) => guardedSetQuickQuestions(qs))
            .catch(() => { });
        }
      } catch (e) {
        if (isAbortError(e)) {
          if (analyzingMessageId) {
            updateMessage(analyzingMessageId, {
              content: { text: t('chat.cancelFailed') },
              status: 'error',
            });
          }
          throw e;
        }

        // 网络异常：更新分析中的消息为失败状态
        if (analyzingMessageId) {
          updateMessage(analyzingMessageId, {
            content: { text: t('chat.answerFailed') },
            status: 'error',
          });
        }
        throw e;
      } finally {
        clearCancelableRequest(controller);
        setLoading(false);
      }
    },
    [extractSessionParams, createGenerationCounter, beginCancelableRequest, clearCancelableRequest, setLoading, addMessage, updateMessage],
  );

  /**
   * 分段模式「分析总结」按钮：调用 summarizeData（流式），结果逐步追加到消息列表末尾。
   * 使用消息自身绑定的 sqlChatId / sqlRecordId，与当前 session 状态无关。
   */
  const handleSummarize = useCallback(
    async (sqlRecordId: string, sqlChatId: string) => {
      const { accessToken } = extractSessionParams();
      const controller = beginCancelableRequest();

      emitGa('ask_question', { question_text: t('chart.analyze'), source: 'summarize' });

      // 立即设置加载状态，显示终止按钮
      setLoading(true);
      setStreaming(true);

      let analyzingMessageId: string | null = null;
      try {
        // 先添加分析中的消息
        analyzingMessageId = addMessage({
          role: 'assistant',
          bizPayload: {
            role: currentRole.id,
            accessToken: sessionInfo?.type === 'dev-center' ? sessionInfo.accessToken : undefined,
            chatId: sessionInfo?.type === 'dev-center' ? sessionInfo.chatId : undefined,
          },
          content: { text: t('chat.analyzing') },
          status: 'sending',
        });

        const summaryEventSource = await summarizeData(sqlChatId, sqlRecordId, accessToken, controller.signal);

        // 使用 Promise 包装 SSE 流式处理，确保所有分支都能正确清理状态（避免按钮卡死）
        await new Promise<void>((resolve) => {
          let finished = false;
          let summaryContent = '';

          const finish = (status: 'success' | 'error' | 'cancelled') => {
            if (finished) return;
            finished = true;
            setStreaming(false);
            setLoading(false);
            summaryEventSource.close();

            if (analyzingMessageId) {
              const updates: Partial<Pick<ChatMessage, 'status' | 'content'>> = { status };
              if (status === 'success') {
                updates.content = { text: summaryContent };
              } else if (status === 'error') {
                updates.content = { text: summaryContent || t('chat.answerFailed') };
              }
              // cancelled 时保留已有内容，仅改状态
              updateMessage(analyzingMessageId, updates);
            }
            resolve();
          };

          // signal abort 时主动触发取消处理，确保状态清理（解决 signal 取消无效问题）
          controller.signal.addEventListener('abort', () => finish('cancelled'), { once: true });

          summaryEventSource.onmessage = (event: MessageEvent) => {
            try {
              const data = JSON.parse(event.data);
              if (data.done) {
                finish('success');
              } else {
                summaryContent += data.text || '';
                if (analyzingMessageId) {
                  updateMessage(analyzingMessageId, {
                    content: { text: summaryContent },
                    status: 'streaming',
                  });
                }
              }
            } catch (e) {
              console.error('Error parsing summary stream:', e);
            }
          };

          summaryEventSource.onerror = (e) => {
            if (controller.signal.aborted) {
              finish('cancelled');
              return;
            }
            console.error('Summary EventSource error:', e);
            finish('error');
          };
        });
      } catch (e) {
        setLoading(false);
        setStreaming(false);

        if (isAbortError(e)) {
          if (analyzingMessageId) {
            updateMessage(analyzingMessageId, {
              status: 'cancelled',
            });
          }
          throw e;
        }

        // 网络异常：更新分析中的消息为失败状态
        if (analyzingMessageId) {
          updateMessage(analyzingMessageId, {
            content: { text: t('chat.answerFailed') },
            status: 'error',
          });
        }
        throw e;
      } finally {
        clearCancelableRequest(controller);
        setLoading(false);
        setStreaming(false);
      }
    },
    [extractSessionParams, beginCancelableRequest, clearCancelableRequest, setLoading, addMessage, updateMessage, setStreaming],
  );

  const handleRetry = useCallback(
    async (messageId: string) => {
      const idx = messages.findIndex((m) => m.id === messageId);
      if (idx <= 0) return;

      const userMsg = messages[idx - 1];
      if (userMsg.role !== 'user' || !userMsg.content.text) return;

      const question = userMsg.content.text;
      const failedMsg = messages[idx];

      emitGa('ask_question', { question_text: question, source: 'retry' });

      // 先将失败消息置为 sending，给用户即时反馈
      updateMessage(messageId, {
        content: { text: '' },
        status: 'sending',
      });

      const params = extractSessionParams();
      const { guardedSetQuickQuestions } = createGenerationCounter();
      const controller = beginCancelableRequest();
      const strategy = getChatFlowStrategy(currentRole.schema);

      /**
       * 用包装后的 addMessage 拦截 strategy 内部的消息添加行为：
       * - 第 1 次调用（user 消息）：忽略，不重复添加
       * - 第 2 次调用（assistant 占位消息）：改为 updateMessage 覆盖失败消息，返回原 messageId
       * - 第 3 次及之后（如 defaultFlow 的总结消息）：正常追加新消息
       */
      let addCallCount = 0;
      const wrappedAddMessage: typeof addMessage = (msg) => {
        addCallCount += 1;
        if (addCallCount === 1) {
          return '';
        }
        if (addCallCount === 2) {
          updateMessage(messageId, { ...msg } as DeepPartial<ChatMessage>);
          return messageId;
        }
        return addMessage(msg);
      };

      try {
        await executeFlow(
          strategy,
          question,
          {
            ...params,
            addMessage: wrappedAddMessage,
            updateMessage,
            setLoading,
            setQuickQuestions: guardedSetQuickQuestions,
            signal: controller.signal,
          },
          {
            timeoutMs: 10 * 60 * 1000,
            onTimeout: () => controller.abort(),
          },
        );
      } catch {
        // 请求失败：恢复原始错误消息，并提示用户
        updateMessage(messageId, {
          content: failedMsg.content,
          status: 'error',
        });
        toast.error(t('retry.failed'));
      } finally {
        clearCancelableRequest(controller);
        setLoading(false);
      }
    },
    [messages, extractSessionParams, createGenerationCounter, beginCancelableRequest, clearCancelableRequest, currentRole.id, currentRole.schema, addMessage, updateMessage, setLoading],
  );

  const canAsk = !isHistoryReadonly && view !== 'history';

  const quickQuestionItems: QuickQuestion[] =
    quickQuestions?.length > 0
      ? quickQuestions.slice(0, 5).map((text, i) => ({
        id: String(i),
        text,
      }))
      : ([] as QuickQuestion[]);

  /** 初始化完成或身份变化后重新查询资格，等待期间隐藏营销入口。 */
  const [showStoreMarketing, setShowStoreMarketing] = useState(false);
  useEffect(() => {
    if (!isReady) {
      setShowStoreMarketing(false);
      return;
    }
    let cancelled = false;
    queryStoreMarketingVisible().then((visible) => {
      if (cancelled) return;
      setShowStoreMarketing(visible);
      if (!visible) setHeaderTab('chat');
    });
    return () => {
      cancelled = true;
      setShowStoreMarketing(false);
    };
  }, [isReady, historyScope]);

  const isMarketingTab = showStoreMarketing && headerTab === 'marketing';

  /** 营销页一旦打开过就一直留在 DOM 里，切走只隐藏 */
  const [marketingMounted, setMarketingMounted] = useState(false);
  useEffect(() => {
    if (isMarketingTab) setMarketingMounted(true);
  }, [isMarketingTab]);

  return (
    <div className="chat-panel">
      <ChatHeader
        config={config}
        onClose={handleClose}
        onFullScreen={handleFullScreen}
        onNewChat={handleNewChat}
        onShowHistory={handleShowHistory}
        isHistoryView={view === 'history'}
        newChatDisabled={isBusy}
        historyDisabled={isBusy}
        showStoreMarketing={showStoreMarketing}
        activeTab={isMarketingTab ? 'marketing' : 'chat'}
        onSelectTab={setHeaderTab}
      />
      {/**
        * 营销页首次进入时才挂载（避免未点击就白白加载 iframe），
        * 之后常驻不卸载，仅用 visible 隐藏 —— 卸载会让营销页整个重载，
        * 丢掉初始化状态、历史滚动位置和进行中的对话。
        */}
      {marketingMounted && <StoreMarketing visible={isMarketingTab} />}
      {!isMarketingTab && view === 'history' && (
        <HistoryPanel onSelect={handleSelectHistory} disabled={isBusy} />
      )}
      {!isMarketingTab && view !== 'history' && (
        <AutoScrollContainer
          className="chat-panel__content"
          deps={[messages, quickQuestions]}
          forceScrollDeps={[sendScrollTick]}
        >
          <MessageList
            messages={messages}
            hostSource={hostSource}
            onRetry={isHistoryReadonly ? undefined : handleRetry}
            onGenerateChart={isHistoryReadonly ? undefined : handleGenerateChart}
            onSummarize={isHistoryReadonly ? undefined : handleSummarize}
          />
          {canAsk && quickQuestionItems?.length > 0 && (
            <QuickQuestions
              questions={quickQuestionItems}
              onSelect={(text) => { void handleSend(text, 'faq'); }}
              disabled={isLoading || isStreaming}
              visible={!impressionOnOpen || isOpen}
            />
          )}
        </AutoScrollContainer>
      )}
      {!isMarketingTab && view !== 'history' && (
        <ChatInput
          hostSource={hostSource}
          onSend={(text) => { void handleSend(text, 'input'); }}
          onTerminate={handleTerminate}
          disabled={isLoading || isCancelling || isStreaming || !canAsk}
          terminateLoading={isCancelling}
          showTerminateButton={canAsk && (isLoading || isStreaming || isCancelling)}
        />
      )}
    </div>
  );
};

export default ChatPanel;
